import { sdk } from '@/lib/api';
import { FetchError } from '@/lib/api/client';
import { queryKeysFactory } from '@/lib/query-key-factory';
import { HttpTypes } from '@/types';
import {
  QueryKey,
  UseInfiniteQueryOptions,
  UseMutationOptions,
  UseQueryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import Cookies from 'js-cookie';

const BLOG_POST_QUERY_KEY = 'blog-post-list' as const;
const BLOG_POST_PREVIEW_QUERY_KEY = 'blog-post-preview-list' as const;

export const blogPostQueryKeys = queryKeysFactory(BLOG_POST_QUERY_KEY);
export const blogPostPreviewQueryKeys = queryKeysFactory(BLOG_POST_PREVIEW_QUERY_KEY);

const isPartner = Cookies.get('_partner_id') !== undefined;

export const useBlogPosts = (
  query: HttpTypes.FindParams,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.AdminBlogPostListResponse,
      FetchError,
      HttpTypes.AdminBlogPostListResponse,
      QueryKey
    >,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: blogPostQueryKeys.list(query),
    queryFn: async () =>
      isPartner ? sdk.partner.blogPosts.list(query) : sdk.admin.blogPosts.list(query),
    ...options,
  });

  return { ...data, ...rest };
};

export const useCreateBlogPost = (
  options?: UseMutationOptions<
    HttpTypes.IResponse<HttpTypes.AdminBlogPostCreateResponse>,
    FetchError,
    HttpTypes.AdminCreateBlogPost
  >
) => {
  return useMutation({
    mutationFn: (payload) =>
      isPartner ? sdk.partner.blogPosts.create(payload) : sdk.admin.blogPosts.create(payload),
    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const useDetailBlogPost = (
  id: string,
  options?: Omit<
    UseQueryOptions<
      HttpTypes.AdminBlogPostResponse,
      FetchError,
      HttpTypes.AdminBlogPostResponse,
      QueryKey
    >,
    'queryFn' | 'queryKey'
  >
) => {
  const { data, ...rest } = useQuery({
    queryKey: blogPostQueryKeys.detail(id),
    queryFn: async () =>
      isPartner ? sdk.partner.blogPosts.detail(id) : sdk.admin.blogPosts.detail(id),
    ...options,
  });

  return { ...data, ...rest };
};

export const useUpdateBlogPost = (
  options?: UseMutationOptions<
    HttpTypes.IResponse<HttpTypes.AdminBlogPostUpdateResponse>,
    FetchError,
    HttpTypes.AdminUpdateBlogPost
  >
) => {
  return useMutation({
    mutationFn: (payload) =>
      isPartner ? sdk.partner.blogPosts.update(payload) : sdk.admin.blogPosts.update(payload),
    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const useDeleteBlogPost = (
  options?: UseMutationOptions<
    HttpTypes.IResponse<HttpTypes.AdminBlogPostDeleteResponse>,
    FetchError,
    string
  >
) => {
  return useMutation({
    mutationFn: (id) =>
      isPartner ? sdk.partner.blogPosts.delete(id) : sdk.admin.blogPosts.delete(id),
    onSuccess(data, variables, context) {
      options?.onSuccess?.(data, variables, context);
    },
    ...options,
  });
};

export const useInfiniteBlogPost = (
  query: HttpTypes.FindParams,
  options?: Omit<
    UseInfiniteQueryOptions<
      HttpTypes.AdminBlogPostListInfiniteResponse,
      FetchError,
      HttpTypes.AdminBlogPostListInfiniteResponse,
      HttpTypes.AdminBlogPostListInfiniteResponse,
      QueryKey
    >,
    'queryFn' | 'queryKey' | 'getNextPageParam' | 'initialPageParam'
  >
) => {
  return useInfiniteQuery({
    queryKey: blogPostPreviewQueryKeys.list(query),
    queryFn: async ({ pageParam = 1 }) => {
      const result = await (isPartner
        ? sdk.partner.blogPosts.list({ ...query, page: pageParam as number })
        : sdk.admin.blogPosts.list({ ...query, page: pageParam as number }));

      return {
        data: result?.data ?? [],
        totalCount: result?.totalCount ?? 0,
      };
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const pageSize = lastPage?.data?.length ?? 0;
      const currentPage = allPages?.length ?? 0;

      if (currentPage * pageSize < Number(lastPage?.totalCount ?? 0)) {
        return currentPage + 1;
      }

      return undefined;
    },
    ...options,
  });
};

export const useDuplicateBlogPost = (options: { onErrors: (error: Error) => void }) => {
  return useMutation({
    mutationFn: async (data: string) => sdk.admin.blogPosts.duplicate(data),
    onError(error) {
      options.onErrors(error);
    },
  });
};
